export type TrustedCaregiverOnboardingStatus = 'INVITED' | 'IN_PROGRESS' | 'ELIGIBILITY_REVIEW' | 'UNDER_REVIEW' | 'COMPLETE';

export const TRUSTED_CAREGIVER_CHECKLIST_FLAG = 'sc-trusted-caregiver-checklist-query-enabled';

export type TrustedCaregiverChecklistItemType = 'PHOTO' | 'IDV' | 'AVAILABILITY' | 'REVIEWS' | 'CPR_CERTIFICATE' | 'VIDEO';

/** A failed IDV attempt is reported as NOT_STARTED so the sitter can retry; there is no failed state.
 *  For IDV, PENDING is awaiting a verdict and cannot be acted on. IDV does not normally return IN_PROGRESS,
 *  so the checklist treats it like NOT_STARTED. */
export type TrustedCaregiverChecklistItemStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'PENDING' | 'COMPLETED';

export type TrustedCaregiverChecklistItem = {
  type: TrustedCaregiverChecklistItemType;
  status: TrustedCaregiverChecklistItemStatus;
};

export type TrustedCaregiver = {
  onboardingStatus: TrustedCaregiverOnboardingStatus;
  checklist: TrustedCaregiverChecklistItem[];
};

export const CHECKLIST_ITEM_ORDER: TrustedCaregiverChecklistItemType[] = [
  'PHOTO',
  'IDV',
  'AVAILABILITY',
  'REVIEWS',
  'VIDEO',
  'CPR_CERTIFICATE',
];

export const CHECKLIST_ITEM_COPY: Record<TrustedCaregiverChecklistItemType, { title: string; content: string }> = {
  PHOTO: {
    title: 'Add a profile photo',
    content: 'Add a clear, friendly photo to help families get to know you.',
  },
  IDV: {
    title: 'Complete your identity verification',
    content:
      'Have a valid government-issued ID and photo ready. Persona, our third-party provider, will guide you through the process.',
  },
  AVAILABILITY: {
    title: 'Update your availability',
    content: 'Let families know when you’re free to work.',
  },
  REVIEWS: {
    title: 'Meet the ratings and reviews requirement',
    content: 'Have at least 3 reviews at 4 stars or higher.',
  },
  VIDEO: {
    title: 'Learn Sittercity’s best practices',
    content: 'Watch our video for tips on maintaining top standards of care.',
  },
  CPR_CERTIFICATE: {
    title: 'Add your CPR/First Aid certification',
    content: 'Include your training in your bio.',
  },
};

export const CHECKLIST_ITEM_STATUS_COPY: Partial<
  Record<
    TrustedCaregiverChecklistItemType,
    Partial<Record<TrustedCaregiverChecklistItemStatus, { title?: string; content: string }>>
  >
> = {
  IDV: {
    PENDING: { title: 'Complete ID Verification', content: 'Under review by our team' },
  },
};

export const COPY = {
  inProgressHeader: 'Your Trusted Caregiver Checklist',
  underReviewHeader: 'Your Trusted Caregiver Status',
  completeHeader: 'You’re a Trusted Sitter',
  eligibilityHeader: 'Maintain your Trusted Caregiver eligibility',
  tipsHeader: 'Maintain your Trusted Caregiver Program status',
  inProgressSubtext: 'Complete these steps to become a Trusted Sitter.',
  underReviewSubtext:
    'Our team is reviewing your profile to ensure you’ve completed all the required steps. Keep an eye on your email for the next steps.',
  completeSubtext:
    'You’re now part of the Trusted Caregiver Program, a new way to help sitters stand out and create more connections. Families will see your badge once our program launches in January 2027.',
  eligibilityReviewSubtext:
    'Your Trusted Caregiver eligibility is under review. Continue meeting these criteria to stay in good standing.',
  learnMoreLabel: 'Learn more about the Trusted Caregiver Program.',
} as const;

/** Maintenance tips shown when onboardingStatus is COMPLETE */
export type CompleteTipAction = 'photo' | 'review' | 'availability' | 'cpr' | 'firstAid';

export type CompleteTip = {
  id: string;
  label: string;
  action?: CompleteTipAction;
};

export const COMPLETE_TIPS: CompleteTip[] = [
  { id: 'response-time', label: 'Respond to messages from families within 24 hours' },
  { id: 'rating', label: 'Maintain an average rating of 4 stars or higher', action: 'review' },
  { id: 'first-aid', label: 'Maintain a First Aid Certification', action: 'firstAid' },
  { id: 'cpr', label: 'Maintain a CPR Certification', action: 'cpr' },
  {
    id: 'availability',
    label: 'Keep your calendar updated so it always shows your availability for the next 90 days.',
    action: 'availability',
  },
  { id: 'cancellation', label: 'Follow the Cancellation and No-Show Policy' },
  { id: 'monitoring', label: 'Pass continuous criminal monitoring' },
  { id: 'photo', label: 'Keep your profile photo up to date', action: 'photo' },
];

export const CONFETTI_SESSION_KEY = 'trusted_caregiver_confetti_shown';

export function isChecklistItemComplete(item: TrustedCaregiverChecklistItem): boolean {
  return item.status === 'COMPLETED';
}

export function computeCompletenessScore(checklist: TrustedCaregiverChecklistItem[]): number {
  if (checklist.length === 0) return 0;
  const completedCount = checklist.filter(isChecklistItemComplete).length;
  return Math.round((completedCount / checklist.length) * 100);
}

type TrustedCaregiverApiPayload = {
  onboardingStatus?: TrustedCaregiverOnboardingStatus | null;
  checklist: TrustedCaregiverChecklistItem[];
};

/** Maps the trustedCaregiver GraphQL payload into presentation state. */
export function mapTrustedCaregiverFromApi(payload: TrustedCaregiverApiPayload | null | undefined): TrustedCaregiver | null {
  if (!payload?.checklist) return null;

  const checklist = payload.checklist;
  const derivedStatus: TrustedCaregiverOnboardingStatus =
    checklist.length > 0 && checklist.every(isChecklistItemComplete) ? 'UNDER_REVIEW' : 'IN_PROGRESS';

  return {
    onboardingStatus: payload.onboardingStatus ?? derivedStatus,
    checklist,
  };
}
